/**
 * The server-backed half of client state.
 *
 * `state.ts` still owns the mock data the unwired screens read. This owns everything the server
 * decides: who you are, what the Beat is doing, and the single-use action token that stands
 * between a click and a mutation.
 *
 * THE CLIENT NEVER COMPUTES AN OUTCOME. It derives two things locally and nothing else:
 *
 *   - Resource bars, from `(value, at, periodMs)` using the SAME `settle()` the server uses.
 *     That is not a prediction, it is the identical pure function over identical inputs.
 *   - What an action will COST, which is deterministic and printed on the button before the tap.
 *
 * The roll, the payout, the Heat and the bust all arrive in the response or they do not exist.
 */

import { create } from 'zustand'
import {
  ACTION_TOKEN_TTL_MS,
  ENERGY_PERIOD_MS,
  NERVE_PERIOD_MS,
  type ActionIntent,
  type Announcement,
  type RankState,
  type BeatState,
  type BootPayload,
  type CatalogItem,
  type Cents,
  type ChatLine,
  type CooloffMethod,
  type CrimeOutcome,
  type CrimesPayload,
  type Currency,
  type DistrictId,
  type FamilyPayload,
  type FamilyPermission,
  type FamilyRole,
  type JobOutcome,
  type JobsPayload,
  type Lineup,
  type LedgerPage,
  type LedgerPayload,
  type MarketAisle,
  type MarketCategory,
  type MorningLine,
  type TerritoryPayload,
  type WardOutcome,
  type WardPayload,
  type FightListPayload,
  type HitlistPayload,
  type PvpIntent,
  type PvpOutcome,
  type WarBoard,
  type MarketPayload,
  type MoneyPayload,
  type NextPayload,
  type PlayerSnapshot,
  type TreasuryState,
  type Upgrade,
  type UpgradeId,
} from '@ashport/shared'
import { beatAtOrNull } from '@ashport/shared/beat'
import { decay, settle } from '@ashport/shared/formulas'
import type { PushKind, PushPayload, ScannerPayload } from '@ashport/shared'
import { ApiError, api, observe, serverNow, type GearPayload, type HeatPayload } from '../net/api'
import { openRoom, type Room } from '../net/socket'
import { currentSubscription, subscribeBrowser, unsubscribeBrowser } from '../pwa'

/** Whole dollars from cents, for a notice line. The screens have `exact`; the store keeps its own. */
const exactCents = (c: number): string => `$${Math.round(c / 100).toLocaleString('en-US')}`

/**
 * `closed` is the Era-over state, and it is separate from `down` on purpose.
 *
 * `ERA_CLOSED` used to fall through to the same `<Down />` screen a dead network line does, so a
 * player arriving at the emotional peak of a twelve-week wipe got an error page. It is the one
 * moment in the product the whole reset design is FOR. `server/src/lib/beat.ts:78` and
 * `server/src/routes/ward.ts:118` are the two places the server says it.
 */
export type Phase = 'booting' | 'anon' | 'ready' | 'down' | 'closed'

/**
 * One live action token and everything it can be spent on.
 *
 * Structurally a `TokenBundle`, and deliberately its own name: a bundle is what the SERVER issued
 * on one read, a grant is what the CLIENT is still holding. The two diverge the moment a mutation
 * answers with a `nextToken`, which is a grant covering exactly one intent and was never a bundle.
 */
export interface Grant {
  token: string
  intents: ActionIntent[]
  expiresAt: number
}

/** The settled, renderable view of the player at one instant. */
export interface Live {
  energy: number
  energyMax: number
  energyAt: number
  nerve: number
  nerveMax: number
  nerveAt: number
  heat: number
  heatAt: number
  heatPeriodMs: number
}

export function live(s: PlayerSnapshot, now: number): Live {
  const e = settle(s.energy.value, s.energy.asOfMs, now, s.energy.periodMs || ENERGY_PERIOD_MS, s.energy.max)
  const n = settle(s.nerve.value, s.nerve.asOfMs, now, s.nerve.periodMs || NERVE_PERIOD_MS, s.nerve.max)
  const h = decay(s.heat.value, s.heat.asOfMs, now, s.heat.periodMs)
  return {
    energy: e.value,
    energyMax: s.energy.max,
    energyAt: e.at,
    nerve: n.value,
    nerveMax: s.nerve.max,
    nerveAt: n.at,
    heat: h.value,
    heatAt: h.at,
    heatPeriodMs: s.heat.periodMs,
  }
}

interface SessionState {
  phase: Phase
  /** Set when the whole app cannot proceed: maintenance, a closed Era, a dead line. */
  fault: string | null
  me: PlayerSnapshot | null
  boot: BootPayload | null
  /** Published once per Era. The Beat is derived locally from it and costs nothing thereafter. */
  schedule: DistrictId[]

  jobs: JobsPayload | null
  crimes: CrimesPayload | null
  money: MoneyPayload | null
  heat: HeatPayload | null
  scanner: ScannerPayload | null
  market: MarketPayload | null
  gear: GearPayload | null
  /** PHASE 23. What the server holds for this account's browsers. Loaded by the card. */
  push: PushPayload | null
  /** PHASE 23. One line under the card's buttons: why a subscribe did not happen, or that it did. */
  pushNote: string | null
  /** PHASE 23. The card is an overlay any shell can open. */
  notifyOpen: boolean
  family: FamilyPayload | null
  /** The rung, the checklist and the house's offer. GAME-DESIGN 6.1 and 6.3. */
  rank: RankState | null
  /**
   * What to do now, at two scales. The first-night beats and the rank checklist, in one read.
   *
   * DESIGN-DELTAS 1t.4: no route in this game is gated, so the FIRST SCREEN has to carry the
   * ordering instead, and this is it. Every beat's `done` is decided on the server against real
   * player state; the client cannot set one, send one back or infer one.
   */
  next: NextPayload | null
  /**
   * The pass track, the badge wall, the Era record and the standings.
   *
   * The screen this feeds carried fifteen hand-typed arrays until Stage 2, including twelve
   * invented people on a standings board and a $9.99 bundle selling two earned-only cosmetics.
   */
  ledger: LedgerPayload | null
  /**
   * The Ward: everybody currently in a cell, priced and odds-checked for this viewer.
   *
   * The game's only failure state had no exit until Stage 3. `bustOutChance` was implemented with
   * zero callers and `bailPrice` had one, a mock display line.
   */
  ward: WardPayload | null
  /**
   * The Fight List, with every published number already resolved for this viewer.
   *
   * The whole PvP resolution layer shipped at Phase 6 close with zero callers and fifty-five
   * commissioned artworks rendered by nothing. This slice is what ends that.
   */
  pvp: FightListPayload | null
  hitlist: HitlistPayload | null
  /** The last fight's result plate, held until the player dismisses it. */
  lastFight: PvpOutcome | null
  /**
   * The territory map: forty blocks, who holds each, and what the viewer's family pays.
   *
   * Every number behind it shipped at Phase 6 close with no caller and no storage. What was
   * missing was the content spine and the wiring, which is the same shape as the eight
   * capabilities Phase 6B stage 2 found built and unreachable.
   */
  territory: TerritoryPayload | null
  /**
   * One block's fight, live, or null when no block is open on the War Room.
   *
   * It is a SEPARATE read from the map on purpose: the map is forty rows on every poll, and a
   * scoreboard for forty blocks nobody is fighting over would be forty joins for one contested
   * street.
   */
  warBoard: WarBoard | null
  /** The last thing a territory verb answered with, for the outcome line. */
  lastTerritory: string | null
  /**
   * The Morning Line: the rack, the Card and what is in your pocket.
   *
   * Energy regenerates 360 a day against a bar of 91 at level 1, so without refills a player has
   * to log in three or more times a day just to avoid wasting regeneration. That is the classic
   * energy-bar trap, and the escape hatch was built, routed and unreachable for a whole phase.
   */
  morning: MorningLine | null
  /** The last bail or bust result, for the outcome card. Cleared when the card is dismissed. */
  lastWard: WardOutcome | null
  /** Your own money history, paged. The cursor is BOTH halves: see `api.ledger`. */
  moneyHistory: LedgerPage | null
  /**
   * The live room, and whether the line is up.
   *
   * The socket is a VIEW of state the HTTP read already owns: every frame patches the slice the
   * read produced. Nothing is authoritative here that is not also fetchable, because every
   * deploy terminates every socket with no grace period and a room that only existed in a frame
   * would be gone.
   */
  room: Room | null
  roomStatus: 'connecting' | 'open' | 'closed'
  /**
   * The 135 authored items, fetched ONCE per manifest version.
   *
   * Immutable for a version by construction, exactly like the Beat schedule, so it is cached
   * against the version it was fetched for and never refetched inside one.
   */
  catalog: Map<string, CatalogItem>
  catalogVer: number
  /**
   * EVERY LIVE ACTION TOKEN, KEYED BY WHAT IT CAN BUY, and until 3 August 2026 there was one slot.
   *
   * One slot works while one board is on screen. Home is eight boards: `NextPanel`, `BeatPanel`,
   * `ProtectionPanel`, `MorningLineCard`, `LaunderCard`, `CoolOffCard`, `FrontsCard`, `JobCard`
   * and `ChatPanel` all read on mount, seven of those reads mint a bundle, they land in whatever
   * order the network returns them, and the LAST one won the slot. So the token sitting in the
   * slot when a player pressed "Launder Cash" was, in the run that found this, `/api/gear`'s
   * `gear.equip,gear.repair,gear.crush,business.collect`. The DO checks the intent inside the
   * `DELETE ... RETURNING`, refuses, and the player gets a 409 and a screen where nothing happens.
   * Laundering, running a job and claiming the Card were all a coin flip on the first screen of
   * the game, which is exactly the three endpoints that were 409ing in the field.
   *
   * A superset test on the observer does not fix it: the seven intent sets are disjoint, so no
   * subset or superset rule distinguishes "another board just overwrote mine" from ordinary
   * navigation. The unit that is wrong is the SLOT. A token is valid for a SET of intents, so the
   * thing to hold is a set of grants and the thing to ask is "which grant covers `money.launder`".
   *
   * This also fixes the second half of the bug, which nobody would have found by clicking once.
   * `nextToken` on a mutation response is minted by `PlayerDO.envelope` for the ONE intent that was
   * just spent, and `shared/types.ts` says so in the field's own comment. The old code did
   * `{ ...s.token, token: r.nextToken }`, keeping the OLD bundle's intent list, so after a
   * successful launder the client believed it held a token good for `money.tier` and `money.fence`
   * as well. It did not. `act()` files it under the one intent it is actually good for.
   */
  grants: Grant[]
  /** The last action's result, for the outcome card. Cleared when the card is dismissed. */
  lastJob: JobOutcome | null
  lastCrime: CrimeOutcome | null
  /** In flight. Exactly one action at a time: the server would refuse the second anyway. */
  busy: boolean
  /** A refusal in voice, shown on the screen that caused it. */
  refusal: string | null
  /**
   * THE SERVER ASKED FOR THE PASSWORD AGAIN, AND NOTHING COULD ANSWER IT.
   *
   * `requireElevation()` guards `POST /family/withdraw` and `POST /market/list`, the two verbs a
   * stolen cookie must not be able to perform on its own. `POST /api/auth/elevate` has worked
   * since Phase 1. There was no method in `net/api.ts` and no control on any screen, so both
   * routes answered NEEDS_ELEVATION to every player who ever pressed them and nothing in the
   * client could clear it. Emptying a family treasury and listing on the market were unreachable
   * for the whole life of the game.
   *
   * When this is true the shell draws the prompt. It is on the STORE rather than on a screen
   * because either verb can be reached from more than one place, and the answer is the same
   * everywhere.
   */
  needsElevation: boolean
  /** Why the last elevation attempt failed, in voice. */
  elevationError: string | null
  /**
   * The other half of `refusal`: an authored line an action ANSWERED with, in voice.
   *
   * `content/house.json` authors six strings and one of them, `taken`, is what the house says the
   * moment it speaks for you. It reached the wire, arrived on the response, and was dropped by
   * `act()`, which bound the mutation result and read only the token off it. The floor's whole
   * payoff had nowhere to land.
   */
  notice: string | null
  /**
   * The standing Lineup, or null. GAME-DESIGN 13.2's in-fiction challenge.
   *
   * Here rather than on a screen because the server refuses EVERY tokened verb while one is open,
   * so a player who was on the Market when it was issued must see it on the Market. `act()` fills
   * it from the refusal that caused it and `clearLineup` empties it once the answer has landed.
   */
  lineup: Lineup | null
  clearLineup: () => void

  start: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  /**
   * A real Turnstile token, from a real widget, and it is not optional.
   *
   * This used to be `api.signup({ ...b, turnstile: '' })` under a comment saying the challenge was
   * not wired in development. It was not wired ANYWHERE: the Gate rendered no widget in its 229
   * lines, so the only reason signup worked was `verifyTurnstile` short-circuiting on the testing
   * secret, and the door would have shut the first day a real one was configured.
   */
  register: (b: {
    handle: string
    email: string
    password: string
    turnstile: string
    tosVersion: number
  }) => Promise<void>
  /** Send the verification letter again. Returns the line the server answered with. */
  resendVerify: () => Promise<string>
  signOut: () => Promise<void>
  loadJobs: () => Promise<void>
  loadCrimes: () => Promise<void>
  loadMoney: () => Promise<void>
  loadHeat: () => Promise<void>
  /** The Scanner: the band, the wanted list and the district pressure. */
  loadScanner: () => Promise<void>
  loadMarket: (aisle: MarketAisle) => Promise<void>
  loadGear: () => Promise<void>
  loadFamily: () => Promise<void>
  loadRank: () => Promise<void>
  /** What to do now. Re-read after anything that could satisfy a beat. */
  loadNext: () => Promise<void>
  /** Put the first-night list away, for good, on every device. */
  dismissFirstNight: () => Promise<void>
  loadLedger: () => Promise<void>
  /**
   * Put on a cosmetic you own, or take one off by passing `null` and the slot.
   *
   * `equipped_cosmetic` had a SELECT and a DELETE and no INSERT anywhere in the repository, so a
   * player could buy a cosmetic and never wear it. DESIGN-DELTAS 1t.13.
   */
  equipCosmetic: (cosmeticId: string | null, slot?: string) => Promise<void>
  loadWard: () => Promise<void>
  loadTerritory: () => Promise<void>
  /** One block's fight, live. Separate from the map because forty scoreboards is thirty-nine too many. */
  loadWarBoard: (blockId: string) => Promise<void>
  /** Take the till into the family vault. Any member may: there is no way to do it wrong. */
  collectTerritory: () => Promise<void>
  /** One Books level, at the published price, refused if the price has moved. */
  buyBooks: (expectCost: number) => Promise<void>
  /** One tick of doors. Up on your own ground, down on somebody else's inside a window. */
  fortifyBlock: (blockId: string) => Promise<void>
  /** Call a crew out, striking one of the four windows out. The defender picks from the other three. */
  declareWar: (blockId: string, strikeSlot: number) => Promise<void>
  /** Name the window. The defender's half of the asymmetry. */
  answerWar: (warId: string, windowIndex: number) => Promise<void>
  /** A raid, against the numbers printed on the button. */
  raidBlock: (blockId: string) => Promise<void>
  /** Give a block back to neutral, publicly. The pressure valve. */
  cedeBlock: (blockId: string) => Promise<void>
  /** A Capo opens a pooled action on a block. */
  callOperation: (blockId: string) => Promise<void>
  /** ONE TAP, and it counts the moment it is made. */
  joinOperation: (opId: string) => Promise<void>
  /** The leader calls it in, or any contributor in the last ten minutes. */
  triggerOperation: (opId: string) => Promise<void>
  loadMorningLine: () => Promise<void>
  /** Claim today's place on the Card. Turning up is always enough. */
  claimCard: () => Promise<void>
  /** Spend a banked grant. Refused above a quarter of the bar, with the reason on the bar. */
  spendRefill: (resource: 'energy' | 'nerve') => Promise<void>
  /** Somebody else's bail, in clean cash. Burned; it never reaches the inmate. */
  loadPvp: () => Promise<void>
  loadHitlist: () => Promise<void>
  clearFight: () => void
  attack: (targetId: string, intent: PvpIntent) => Promise<void>
  postBounty: (targetId: string, amount: number, note: string) => Promise<void>
  nominateSleep: (hour: number) => Promise<void>
  bailOut: (playerId: string) => Promise<void>
  /** Four Nerve against the chance printed on the button. */
  bustOut: (playerId: string) => Promise<void>
  /** Your own way out, in dirty, at a multiple that climbs with every use inside a day. */
  selfBail: () => Promise<void>
  /** Off the list, or back on it. Free and reversible. */
  sealCell: (on: boolean) => Promise<void>
  /** Walk the money history. Passing no cursor starts again from the newest row. */
  loadMoneyHistory: (cursor?: { before: number; beforeId: string }) => Promise<void>
  /** Claim the next rung once its checklist clears. Refused WITH the checklist, never a padlock. */
  claimRank: () => Promise<void>
  /** Take the NPC Sponsor of Record's vouch. A floor, not a path. */
  takeHouseVouch: () => Promise<void>
  /** PHASE 24. Draw the Line, once an Era. */
  drawLine: (line: 'energy' | 'nerve') => Promise<void>
  loadCatalog: () => Promise<void>
  joinRoom: () => void
  leaveRoom: () => void
  /** `times` is 1 to `JOB_RUN_MAX`: one tap, that many runs, one plate. Phase 20. */
  runJob: (chainId: DistrictId, step: number, times?: number) => Promise<void>
  commitCrime: (crimeId: string) => Promise<void>
  /** Move to another district. Free, instant, and the one thing that moves the home bonus. */
  setDistrict: (districtId: DistrictId) => Promise<void>

  launder: (amount: Cents) => Promise<void>
  buyTier: (tier: number) => Promise<void>
  fence: (slotId: string, qty: number) => Promise<void>
  coolOff: (method: CooloffMethod, points: number) => Promise<void>
  listItem: (category: MarketCategory, slotId: string, qty: number, unitPrice: Cents) => Promise<void>
  buyItem: (category: MarketCategory, itemId: string, qty: number, maxUnitPrice: Cents) => Promise<void>
  cancelListing: (category: MarketCategory, listingId: string) => Promise<void>
  buyBlack: (category: MarketCategory, itemId: string, qty: number, expectUnitPrice: Cents) => Promise<void>
  equip: (slotId: string, itemId: string) => Promise<void>
  repair: (slotId: string, points: number) => Promise<void>
  crush: (slotId: string, itemId: string, qty: number) => Promise<void>
  collectFronts: () => Promise<void>
  /** PHASE 22. Level one front. The server prices it and says why not, in voice. */
  upgradeFront: (slotId: string) => Promise<void>

  /* ----------------------------------------------------------------- push, Phase 23 */
  loadPush: () => Promise<void>
  /** Ask the browser, then tell the server. The note says what happened either way. */
  enablePush: (kinds: PushKind[]) => Promise<void>
  disablePush: () => Promise<void>
  setPushKinds: (kinds: PushKind[]) => Promise<void>
  pingPush: () => Promise<void>
  openNotify: () => void
  closeNotify: () => void

  createFamily: (name: string, tag: string, motto: string) => Promise<void>
  applyToFamily: (familyId: string, pitch: string) => Promise<void>
  decideApplication: (playerId: string, accept: boolean) => Promise<void>
  setFamilyRole: (playerId: string, role: FamilyRole) => Promise<void>
  setFamilyCrew: (playerId: string, crewId: string | null) => Promise<void>
  kickMember: (playerId: string, reason: string) => Promise<void>
  leaveFamily: () => Promise<void>
  grantPermission: (playerId: string, permission: FamilyPermission, grant: boolean) => Promise<void>
  depositToVault: (currency: Currency, amount: Cents) => Promise<void>
  withdrawFromVault: (currency: Currency, amount: Cents) => Promise<void>
  buyFamilyUpgrade: (upgradeId: UpgradeId, expectCost: Cents) => Promise<void>
  setTithe: (pct: number) => Promise<void>
  setFamilySettings: (recruiting: boolean, minLevel: number, motto: string) => Promise<void>
  postFamilyChat: (body: string) => Promise<void>
  announce: (body: string, pinned: boolean) => Promise<void>
  giftNerve: (playerId: string) => Promise<void>
  offerVouch: (playerId: string) => Promise<void>
  acceptVouch: (id: string, witnessId: string) => Promise<void>

  clearOutcome: () => void
  clearWard: () => void
  clearRefusal: () => void
  /** Re-enter the password, then replay the action that asked for it. */
  elevate: (password: string) => Promise<void>
  /** Walk away from the prompt. The action that raised it is dropped. */
  cancelElevation: () => void
  clearNotice: () => void
  beatNow: (now: number) => BeatState | null
}

export const useSession = create<SessionState>((set, get) => ({
  phase: 'booting',
  fault: null,
  me: null,
  boot: null,
  schedule: [],
  jobs: null,
  crimes: null,
  family: null,
  rank: null,
  next: null,
  room: null,
  roomStatus: 'closed',
  money: null,
  moneyHistory: null,
  ledger: null,
  ward: null,
  pvp: null,
  hitlist: null,
  lastFight: null,
  territory: null,
  warBoard: null,
  lastTerritory: null,
  lastWard: null,
  morning: null,
  heat: null,
  scanner: null,
  market: null,
  gear: null,
  push: null,
  pushNote: null,
  notifyOpen: false,
  catalog: new Map(),
  catalogVer: 0,
  grants: [],
  lastJob: null,
  lastCrime: null,
  busy: false,
  refusal: null,
  needsElevation: false,
  elevationError: null,
  notice: null,
  lineup: null,
  clearLineup: () => set({ lineup: null }),

  /** One call on mount. A 401 is not an error here, it is the signed-out state. */
  start: async () => {
    try {
      const b = await api.boot()
      set({
        phase: 'ready',
        fault: null,
        me: b.player,
        boot: b,
        schedule: b.beatSchedule,
        /*
         * The Lineup arrives on BOOT, and the loop does not close without it. GAME-DESIGN 13.2.
         *
         * A Lineup has to be ISSUED before the server refuses anything on account of one, and
         * `act()` fetches it on that refusal. That is a chicken and egg: without this line nothing
         * issues one, so nothing is ever refused, so the interstitial is unreachable by playing.
         * It compiled, its tests passed against the endpoint directly, and rendering it in a
         * browser is what found it.
         */
        lineup: b.lineup,
      })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'UNAUTHENTICATED') {
        set({ phase: 'anon', fault: null })
        return
      }
      if (e instanceof ApiError && e.code === 'ERA_CLOSED') {
        // The books are closed, which is a destination rather than a failure. The message is the
        // server's, in voice, and the screen that renders it can still read `/api/ledger/reveal`
        // because that route does not touch the Beat.
        set({ phase: 'closed', fault: e.message })
        return
      }
      set({ phase: 'down', fault: e instanceof Error ? e.message : 'The line went dead.' })
    }
  },

  signIn: async (email, password) => {
    await api.login({ email, password })
    await get().start()
  },

  register: async (b) => {
    await api.signup(b)
    await get().start()
  },

  /**
   * Ask for the verification letter again.
   *
   * It does NOT re-boot afterwards. The account is still unverified until the link is followed, so
   * the notice stays up, which is the honest state and also the one that stops a player pressing
   * this button in a loop believing it did something.
   */
  resendVerify: async () => (await api.resendVerify()).message,

  signOut: async () => {
    await api.logout().catch(() => undefined)
    set({ phase: 'anon', me: null, boot: null, jobs: null, crimes: null, grants: [] })
  },

  // The token bundle and the player slice ride on the ENVELOPE, not the payload, so the
  // observer installed below applies them. These only have to store the screen's own data.
  loadJobs: async () => set({ jobs: await api.jobs() }),

  loadCrimes: async () => set({ crimes: await api.crimes() }),

  loadMoney: async () => set({ money: await api.money() }),

  loadHeat: async () => set({ heat: await api.heat() }),

  loadScanner: async () => set({ scanner: await api.scanner() }),

  /*
   * BOTH AT ONCE. These two were awaited in series and the board does not depend on the catalogue.
   *
   * `/api/market/catalog` is 54 KB, the largest read in the game, and it was blocking the board
   * behind it on every first visit to the Market: two full round trips before a single listing
   * could paint, which is the second and a half the Black Market took to appear. They are
   * independent reads and now they race. The catalogue call is a no-op after the first one inside
   * a manifest version, so from the second aisle onwards this is one round trip.
   */
  loadMarket: async (aisle) => {
    const [, market] = await Promise.all([get().loadCatalog(), api.market(aisle)])
    set({ market })
  },

  loadGear: async () => {
    const [, gear] = await Promise.all([get().loadCatalog(), api.gear()])
    set({ gear })
  },

  /**
   * The whole Families surface, in one read.
   *
   * Also the read that mints the token for every button on the screen, which is why a screen
   * calls it on mount rather than relying on whatever the last mutation left behind.
   */
  loadFamily: async () => {
    set({ family: await api.family() })
  },

  loadRank: async () => set({ rank: await api.rank() }),

  loadNext: async () => set({ next: await api.next() }),

  dismissFirstNight: async () => {
    if (get().busy) return
    await act(set, get, 'onboarding.dismiss', (t) => api.dismissFirstNight(t), ['next'])
  },

  /**
   * The Ledger screen, in one read. Pure: it issues no action token because every button on that
   * screen is dead until a payment provider exists.
   */
  loadLedger: async () => set({ ledger: await api.ledger() }),

  /**
   * Put on something you own, or take it off.
   *
   * It holds its own token for the reason `rankToken` does: the Ledger screen already spends a
   * `ledger.purchase` bundle it owns, so a second one in the shared slot would be swapped out from
   * under every other board. The read is re-run afterwards rather than patching `wardrobe` from
   * the response, because the response's wardrobe is authoritative and the re-read costs one call
   * on a screen people sit on.
   */
  equipCosmetic: async (cosmeticId, slot) => {
    if (get().busy) return
    await act(set, get, 'ledger.equip', (t) => api.equipCosmetic(t, cosmeticId, slot), ['ledger'])
  },

  loadWard: async () => set({ ward: await api.ward() }),

  loadPvp: async () => set({ pvp: await api.pvp() }),
  loadHitlist: async () => set({ hitlist: await api.hitlist() }),
  clearFight: () => set({ lastFight: null }),

  /**
   * One of the three intents, declared before the roll.
   *
   * `act()`'s four steps and nothing else. The outcome is KEPT rather than dropped, because a
   * fight answers with a result plate, a headline and a body that no other read can reproduce:
   * the roll happened once and this response is the only place it is described.
   */
  attack: async (targetId, intent) => {
    if (get().busy) return
    const out = await act(set, get, 'pvp.attack', (t) => api.attack(t, targetId, intent), [
      'pvp',
      'hitlist',
    ])
    if (out) set({ lastFight: out.data })
  },

  /*
   * A BOUNTY IS A `pvp.bounty` TOKEN, and it used to be spent from `hitlist.token`.
   *
   * `GET /api/pvp/hitlist` mints a one-intent bundle and puts it on the payload as well as on the
   * envelope, and this action read the payload copy. That was live for as long as the board was,
   * and it went stale the instant ANY read landed after it, because the board is not re-read on
   * navigation. Posting a bounty answered 409 in the field. It is a grant like every other one now.
   */
  postBounty: async (targetId, amount, note) => {
    if (get().busy) return
    await act(set, get, 'pvp.bounty', (t) => api.postBounty(t, targetId, amount, note), [
      'hitlist',
      'pvp',
    ])
  },

  nominateSleep: async (hour) => {
    if (get().busy) return
    await act(set, get, 'pvp.sleep', (t) => api.nominateSleep(t, hour), ['pvp'])
  },

  loadTerritory: async () => {
    const r = await api.territory()
    set({ territory: r })
  },

  loadWarBoard: async (blockId) => set({ warBoard: await api.warBoard(blockId) }),

  /*
   * THE TERRITORY VERBS.
   *
   * Every one of them is `act()`'s four steps and nothing else: send with the screen's token, swap
   * in the one the response returned, re-read the boards that changed, and DROP the token on a
   * refusal rather than reusing a nonce the server has already consumed. The screen computes no
   * outcome; it computes a COST, which is deterministic and printed on the button before the tap.
   */
  collectTerritory: async () => {
    if (get().busy) return
    const out = await act(set, get, 'territory.collect', (t) => api.collectTerritory(t), ['territory'])
    if (out) {
      set({
        lastTerritory: `Collected ${out.data.collected / 100} over ${out.data.hours} hours from ${out.data.blocks} blocks.`,
      })
    }
  },

  buyBooks: async (expectCost) => {
    if (get().busy) return
    const out = await act(set, get, 'family.books', (t) => api.buyBooks(t, expectCost), ['territory'])
    if (out) set({ lastTerritory: `The books are at ${out.data.books}.` })
  },

  fortifyBlock: async (blockId) => {
    if (get().busy) return
    const out = await act(set, get, 'territory.fortify', (t) => api.fortify(t, blockId), ['territory'])
    if (out) {
      set({
        lastTerritory: out.data.raised
          ? `The doors are ${out.data.fortTierName}.`
          : `You took a tier off. The doors are ${out.data.fortTierName}.`,
      })
    }
  },

  declareWar: async (blockId, strikeSlot) => {
    if (get().busy) return
    const out = await act(
      set,
      get,
      'war.declare',
      (t) => api.declareWar(t, blockId, strikeSlot),
      ['territory'],
    )
    if (out) set({ lastTerritory: 'The call is out, and the whole city can read it.' })
  },

  answerWar: async (warId, windowIndex) => {
    if (get().busy) return
    const out = await act(
      set,
      get,
      'war.answer',
      (t) => api.answerWar(t, warId, windowIndex),
      ['territory'],
    )
    if (out) set({ lastTerritory: `You named ${out.data.label}.` })
  },

  raidBlock: async (blockId) => {
    if (get().busy) return
    const out = await act(set, get, 'territory.raid', (t) => api.raidBlock(t, blockId), ['territory'])
    if (out) {
      const d = out.data
      set({
        lastTerritory: d.captured
          ? 'It is yours, and it is held under siege until the next window.'
          : d.chipped
            ? 'The door held. A hinge went with it.'
            : d.heldOnScore
              ? `Control is at zero and you are short on the board: ${d.yourScore} against ${d.flipAt}.`
              : `Knocked ${d.knocked} off. Control is at ${d.control}.`,
      })
    }
  },

  cedeBlock: async (blockId) => {
    if (get().busy) return
    const out = await act(set, get, 'territory.abandon', (t) => api.cedeBlock(t, blockId), ['territory'])
    if (out) set({ lastTerritory: 'You gave it up, publicly.' })
  },

  callOperation: async (blockId) => {
    if (get().busy) return
    const out = await act(set, get, 'war.operation', (t) => api.callOperation(t, blockId), ['territory'])
    if (out) set({ lastTerritory: 'The Operation is open. One tap each.' })
  },

  joinOperation: async (opId) => {
    if (get().busy) return
    const out = await act(set, get, 'war.operation', (t) => api.joinOperation(t, opId), ['territory'])
    // "See that it counted" is the acceptance criterion of the whole stage, so the answer says so
    // in the number rather than in a spinner that stops.
    if (out) {
      set({
        lastTerritory: `You are in. That is ${out.data.score} on the board, and it counts whether or not it goes off.`,
      })
    }
  },

  triggerOperation: async (opId) => {
    if (get().busy) return
    const out = await act(set, get, 'war.operation', (t) => api.triggerOperation(t, opId), ['territory'])
    if (out) {
      set({
        lastTerritory: `${out.data.contributors} went in and knocked ${out.data.knocked} off.`,
      })
    }
  },

  loadMorningLine: async () => set({ morning: await api.morningLine() }),

  claimCard: async () => {
    if (get().busy) return
    await act(set, get, 'money.refill', (t) => api.claimCard(t), ['morning'])
  },

  spendRefill: async (resource) => {
    if (get().busy) return
    await act(set, get, 'money.refill', (t) => api.spendRefill(t, resource), ['morning', 'heat'])
  },

  bailOut: async (playerId) => {
    if (get().busy) return
    const out = await act(set, get, 'ward.bail', (t) => api.bailOut(t, playerId), ['ward', 'money'])
    if (out) set({ lastWard: out.data })
  },

  bustOut: async (playerId) => {
    if (get().busy) return
    const out = await act(set, get, 'ward.bust', (t) => api.bustOut(t, playerId), ['ward'])
    if (out) set({ lastWard: out.data })
  },

  selfBail: async () => {
    if (get().busy) return
    const out = await act(set, get, 'ward.selfbail', (t) => api.selfBail(t), ['ward', 'money'])
    if (out) set({ lastWard: out.data })
  },

  sealCell: async (on) => {
    if (get().busy) return
    await act(set, get, 'ward.seal', (t) => api.sealCell(t, on), ['ward'])
  },

  /**
   * One page of the money history, appended when a cursor is passed and replaced when it is not.
   *
   * The cursor is BOTH halves and that is not decoration: laundering writes a dirty row and a
   * clean row in the same millisecond by construction, so `at` alone is not a position, and a
   * page boundary landing inside that millisecond used to skip the rest of it.
   */
  loadMoneyHistory: async (cursor) => {
    const page = await api.moneyHistory(cursor)
    const prev = get().moneyHistory
    set({
      moneyHistory:
        cursor && prev ? { ...page, rows: [...prev.rows, ...page.rows] } : page,
    })
  },

  /**
   * Open the room, or leave it open.
   *
   * Idempotent: a re-render must not open a second socket, and a player with no family has no
   * room to open. Everything the room pushes is a patch on the slice loadFamily already
   * produced, so a dropped line degrades to a stale screen rather than a broken one.
   */
  joinRoom: () => {
    const s = get()
    const path = s.family?.socketPath ?? null
    if (path === null || s.room !== null) return
    const room = openRoom({
      path,
      onStatus: (roomStatus) => set({ roomStatus }),
      // A resync means the replay ring could not cover the gap, so local room state is replaced
      // outright rather than patched. Re-reading is the honest way to do that.
      onResync: () => {
        void get().loadFamily().catch(() => undefined)
      },
      onFrame: (t, payload) => {
        const cur = get().family
        if (cur === null) return
        if (t === 'chat') {
          const line = payload as ChatLine
          const me = get().me?.playerId ?? ''
          // Deduplicated on seq, because the poster gets the line twice: once in the response to
          // their own POST and once down the socket with everybody else.
          if (cur.chat.some((l) => l.seq === line.seq)) return
          set({
            family: { ...cur, chat: [...cur.chat, { ...line, me: line.playerId === me }].slice(-200) },
          })
          return
        }
        if (t === 'treasury' && cur.overview !== null) {
          const st = payload as TreasuryState
          set({
            family: {
              ...cur,
              overview: {
                ...cur.overview,
                treasuryDirty: st.dirty,
                treasuryClean: st.clean,
                tithePct: st.tithePct,
              },
            },
          })
          return
        }
        if (t === 'upgrade') {
          const up = payload as Upgrade
          set({
            family: {
              ...cur,
              upgrades: cur.upgrades.map((u) => (u.upgradeId === up.upgradeId ? up : u)),
            },
          })
          return
        }
        if (t === 'announce') {
          const a = payload as Announcement
          if (cur.announcements.some((x) => x.id === a.id)) return
          set({ family: { ...cur, announcements: [a, ...cur.announcements].slice(0, 20) } })
          return
        }
        if (t === 'roster') {
          // A roster change moves more than one number (the count, the crew tallies, the power),
          // so it re-reads rather than patching four things and getting one of them wrong.
          void get().loadFamily().catch(() => undefined)
        }
      },
    })
    set({ room, roomStatus: 'connecting' })
  },

  leaveRoom: () => {
    const s = get()
    s.room?.close()
    set({ room: null, roomStatus: 'closed' })
  },

  /** Fetched once per manifest version, then never again inside that version. */
  loadCatalog: async () => {
    const s = get()
    const want = s.boot?.manifestVer ?? 0
    if (s.catalogVer === want && s.catalog.size > 0) return
    const payload = await api.catalog()
    set({
      catalog: new Map(payload.items.map((i) => [i.id, i])),
      catalogVer: payload.manifestVer,
    })
  },

  /*
   * THE TWO OLDEST VERBS, FOLDED BACK ONTO `act()`.
   *
   * These two were written before `act` existed and kept their own hand-rolled copy of it: take
   * the shared token, send, swap, re-read. Three things came free from deleting the copy. The
   * token is looked up by intent rather than taken from a slot every other board also writes,
   * which is what was 409ing `/api/jobs/run`. A Lineup issued mid-session now reaches a player who
   * is standing on Jobs or Crimes, because only `act` knows to go and fetch the interstitial. And
   * an authored refusal is shown the same way here as everywhere else.
   *
   * The outcome is KEPT, because a job and a crime each answer with a roll that happened once and
   * is described nowhere else.
   */
  runJob: async (chainId, step, times = 1) => {
    if (get().busy) return
    // The chain state changed (runs, mastery, the gate on the next beat), so the board is re-read.
    // That is not a second source of truth: the outcome is already authoritative.
    const out = await act(set, get, 'job.run', (t) => api.runJob(t, chainId, step, times), ['jobs'])
    if (out) {
      set({ lastJob: out.data })
      await wearDrops(set, get, out.data.drops)
    }
  },

  commitCrime: async (crimeId) => {
    if (get().busy) return
    const out = await act(set, get, 'crime.commit', (t) => api.commitCrime(t, crimeId), ['crimes'])
    if (out) {
      set({ lastCrime: out.data })
      await wearDrops(set, get, out.data.drops)
    }
  },

  /* ------------------------------------------------------------------ money */

  /**
   * Every mutation below shares one shape, and the shape is the point.
   *
   * Name the INTENT, send the gesture, and RE-READ the screen. `act()` finds the token that covers
   * the intent, fetches the read that mints one if there is none, drops it whether the action
   * landed or not, and files the `nextToken` under the one intent the server minted it for. The
   * re-read is not a second source of truth: the envelope already carried the authoritative player
   * slice and the observer applied it. It exists because the BOARD changed, and the board is not
   * in the envelope.
   */
  /**
   * MOVE. `api.setDistrict` existed with zero callers and no store action at all.
   *
   * Where a player stands decides two live numbers: `crimePayout` pays 0.9x outside a crime's home
   * district, and the Beat multiplies bust odds by 1.35 in the district it is running in against
   * 0.85 everywhere else. Both were pinned to whatever district the account was created in, for
   * the whole Era, with no control anywhere in the game to change it.
   *
   * The board and the Beat panel are both re-read, because the crime list and the odds on it are
   * both district-scoped and a move that left either stale would show a player a price they
   * cannot get.
   */
  setDistrict: async (districtId) => {
    if (get().busy) return
    await act(set, get, 'player.district', (t) => api.setDistrict(t, districtId), ['crimes'])
  },

  launder: async (amount) => {
    const s = get()
    if (!s.money || s.busy) return
    await act(set, get, 'money.launder', (t) => api.launder(t, amount, s.money!.rateBp), ['money'])
  },

  buyTier: async (tier) => {
    if (get().busy) return
    await act(set, get, 'money.tier', (t) => api.buyTier(t, tier), ['money'])
  },

  fence: async (slotId, qty) => {
    const s = get()
    if (!s.money || s.busy) return
    await act(set, get, 'money.fence', (t) => api.fence(t, slotId, qty, s.money!.fenceFeeBp), [
      'money',
      'gear',
    ])
  },

  coolOff: async (method, points) => {
    const s = get()
    if (!s.heat || s.busy) return
    const price = method === 'bribe' ? s.heat.costPerPoint : null
    await act(set, get, 'heat.cooloff', (t) => api.coolOff(t, method, points, price), ['heat', 'money'])
  },

  /* ----------------------------------------------------------------- market */

  /*
   * The category is not sent and never was. The SERVER reads the shard out of the escrowed item,
   * which is what makes "you cannot list a car in the weapons book" a property of the statement
   * rather than a check somebody has to remember. It stays on the signature because the caller
   * knows it and a listing verb that took no category would read as if the book had one.
   */
  listItem: async (_category, slotId, qty, unitPrice) => {
    if (get().busy) return
    await act(set, get, 'market.list', (t) => api.listItem(t, slotId, qty, unitPrice), [
      'market',
      'gear',
    ])
  },

  buyItem: async (category, itemId, qty, maxUnitPrice) => {
    if (get().busy) return
    const out = await act(
      set,
      get,
      'market.buy',
      (t) => api.buyItem(t, category, itemId, qty, maxUnitPrice),
      ['market', 'gear'],
    )
    if (out) await wearIfEmpty(set, get, category, itemId)
  },

  cancelListing: async (category, listingId) => {
    if (get().busy) return
    await act(set, get, 'market.cancel', (t) => api.cancelListing(t, category, listingId), [
      'market',
      'gear',
    ])
  },

  buyBlack: async (category, itemId, qty, expectUnitPrice) => {
    if (get().busy) return
    const out = await act(set, get, 'market.black', (t) => api.buyBlack(t, category, itemId, qty, expectUnitPrice), [
      'market',
      'gear',
    ])
    if (out) await wearIfEmpty(set, get, category, itemId)
  },

  /* ------------------------------------------------------------------- gear */

  equip: async (slotId, itemId) => {
    if (get().busy) return
    await act(set, get, 'gear.equip', (t) => api.equip(t, slotId, itemId), ['gear'])
  },

  repair: async (slotId, points) => {
    if (get().busy) return
    await act(set, get, 'gear.repair', (t) => api.repair(t, slotId, points), ['gear'])
  },

  crush: async (slotId, itemId, qty) => {
    if (get().busy) return
    await act(set, get, 'gear.crush', (t) => api.crush(t, slotId, itemId, qty), ['gear'])
  },

  collectFronts: async () => {
    if (get().busy) return
    await act(set, get, 'business.collect', (t) => api.collectFronts(t), ['gear', 'money'])
  },

  /* ----------------------------------------------------------------- push, Phase 23 */

  loadPush: async () => {
    const sub = await currentSubscription()
    set({ push: await api.push(sub?.endpoint ?? null) })
  },

  enablePush: async (kinds) => {
    if (get().busy) return
    set({ busy: true, pushNote: null })
    try {
      const key = get().push?.publicKey ?? (await api.push(null)).publicKey
      if (!key) {
        set({ pushNote: 'Push is not set up on this deployment yet.' })
        return
      }
      const r = await subscribeBrowser(key)
      if (!r.sub) {
        set({ pushNote: r.why })
        return
      }
      await api.pushSubscribe(r.sub, kinds)
      set({ push: await api.push(r.sub.endpoint ?? null), pushNote: 'This device will hear from the city.' })
    } catch (e) {
      set({ pushNote: e instanceof ApiError ? e.message : 'The server did not take the subscription.' })
    } finally {
      set({ busy: false })
    }
  },

  disablePush: async () => {
    if (get().busy) return
    set({ busy: true, pushNote: null })
    try {
      const endpoint = await unsubscribeBrowser()
      if (endpoint) await api.pushUnsubscribe(endpoint).catch(() => undefined)
      set({ push: await api.push(null), pushNote: 'This device is off the list.' })
    } finally {
      set({ busy: false })
    }
  },

  setPushKinds: async (kinds) => {
    if (get().busy) return
    const sub = await currentSubscription()
    if (!sub?.endpoint) return
    set({ busy: true })
    try {
      await api.pushSubscribe(sub, kinds)
      set({ push: await api.push(sub.endpoint) })
    } catch (e) {
      set({ pushNote: e instanceof ApiError ? e.message : 'The switch did not take.' })
    } finally {
      set({ busy: false })
    }
  },

  pingPush: async () => {
    if (get().busy) return
    set({ busy: true, pushNote: null })
    try {
      const r = await api.pushPing()
      set({
        pushNote: r.queued
          ? 'Sent. It arrives inside a minute, and on a phone it lands even with the game closed.'
          : 'One went out in the last ten seconds. Give it a moment.',
      })
      const sub = await currentSubscription()
      set({ push: await api.push(sub?.endpoint ?? null) })
    } finally {
      set({ busy: false })
    }
  },

  openNotify: () => set({ notifyOpen: true, pushNote: null }),
  closeNotify: () => set({ notifyOpen: false }),

  upgradeFront: async (slotId) => {
    if (get().busy) return
    const out = await act(set, get, 'business.upgrade', (t) => api.upgradeFront(t, slotId), ['gear', 'money'])
    if (out) {
      const o = out.data
      set({
        notice: `${o.name} is level ${o.level}. ${o.materialQty} ${o.materialName}${o.materialQty === 1 ? '' : 's'} and ${exactCents(o.cost)} clean went into it${o.collected > 0 ? `, and ${exactCents(o.collected)} came out of the till first` : ''}.`,
      })
    }
  },

  /* ------------------------------------------------------------------------ family */

  /*
   * Every one of these is the same shape and the shape is deliberate: name the intent, act, and
   * re-read the family board afterwards whether it landed or not. A refusal usually means somebody
   * else moved first, and showing the board they beat you to is worse than nothing.
   *
   * `busy` is what serialises them. One grant covers the whole family intent set and is single
   * use, so firing two at once would spend the same nonce twice and the server would refuse the
   * second. The second would now go and mint a fresh one rather than fail, which is better and is
   * still not a reason to let two family writes race.
   */
  createFamily: async (name, tag, motto) => {
    if (get().busy) return
    await act(set, get, 'family.create', (t) => api.createFamily(t, name, tag, motto), ['family'])
  },
  applyToFamily: async (familyId, pitch) => {
    if (get().busy) return
    await act(set, get, 'family.apply', (t) => api.applyToFamily(t, familyId, pitch), ['family'])
  },
  decideApplication: async (playerId, accept) => {
    if (get().busy) return
    await act(set, get, 'family.decide', (t) => api.decideApplication(t, playerId, accept), ['family'])
  },
  setFamilyRole: async (playerId, role) => {
    if (get().busy) return
    await act(set, get, 'family.role', (t) => api.setFamilyRole(t, playerId, role), ['family'])
  },
  setFamilyCrew: async (playerId, crewId) => {
    if (get().busy) return
    await act(set, get, 'family.crew', (t) => api.setFamilyCrew(t, playerId, crewId), ['family'])
  },
  kickMember: async (playerId, reason) => {
    if (get().busy) return
    await act(set, get, 'family.kick', (t) => api.kickMember(t, playerId, reason), ['family'])
  },
  leaveFamily: async () => {
    if (get().busy) return
    // The room goes first. A socket held to a family you just left is refused on the next
    // reconnect anyway, and closing it here means the screen never renders a room it is not in.
    get().leaveRoom()
    await act(set, get, 'family.leave', (t) => api.leaveFamily(t), ['family'])
  },
  grantPermission: async (playerId, permission, grant) => {
    if (get().busy) return
    await act(
      set,
      get,
      'family.grant',
      (t) => api.grantPermission(t, playerId, permission, grant),
      ['family'],
    )
  },
  depositToVault: async (currency, amount) => {
    if (get().busy) return
    await act(set, get, 'family.deposit', (t) => api.depositToVault(t, currency, amount), ['family', 'money'])
  },
  withdrawFromVault: async (currency, amount) => {
    if (get().busy) return
    await act(set, get, 'family.withdraw', (t) => api.withdrawFromVault(t, currency, amount), ['family', 'money'])
  },
  buyFamilyUpgrade: async (upgradeId, expectCost) => {
    if (get().busy) return
    await act(set, get, 'family.upgrade', (t) => api.buyFamilyUpgrade(t, upgradeId, expectCost), ['family'])
  },
  setTithe: async (pct) => {
    if (get().busy) return
    await act(set, get, 'family.tithe', (t) => api.setTithe(t, pct), ['family'])
  },
  setFamilySettings: async (recruiting, minLevel, motto) => {
    if (get().busy) return
    await act(
      set,
      get,
      // `POST /family/settings` is guarded by `withToken('family.tithe')`: the Boss's two dials
      // share one intent because they are one permission. Named here as the server names it.
      'family.tithe',
      (t) => api.setFamilySettings(t, recruiting, minLevel, motto),
      ['family'],
    )
  },
  postFamilyChat: async (body) => {
    if (get().busy) return
    await act(set, get, 'family.chat', (t) => api.postFamilyChat(t, body), ['family'])
  },
  announce: async (body, pinned) => {
    if (get().busy) return
    await act(set, get, 'family.announce', (t) => api.announce(t, body, pinned), ['family'])
  },
  giftNerve: async (playerId) => {
    if (get().busy) return
    await act(set, get, 'family.gift', (t) => api.giftNerve(t, playerId), ['family', 'money'])
  },
  offerVouch: async (playerId) => {
    if (get().busy) return
    await act(set, get, 'family.sponsor', (t) => api.offerVouch(t, playerId), ['family', 'money'])
  },
  acceptVouch: async (id, witnessId) => {
    if (get().busy) return
    /*
     * A vouch moves the rung as well as the roster, so both boards are re-read. GAME-DESIGN 6.3
     * step 4: "On completion the sponsee becomes a Soldier."
     *
     * The ORDER used to matter here, because the last read of the refresh loop was the one whose
     * bundle landed in the single token slot and a Families button ending on the rank read was a
     * Families button whose next tap was refused. It does not matter any more: the two bundles are
     * disjoint and both are held. The order is kept because a promotion is the more interesting of
     * the two facts and re-reading it first gets it on screen first.
     */
    await act(set, get, 'family.vouch', (t) => api.acceptVouch(t, id, witnessId), ['rank', 'family'])
  },

  /*
   * The rank surface mints its OWN token, because the ladder is not the family screen.
   *
   * A player with no family still has a rung, still has a checklist and is exactly the player
   * the house exists for, so these two cannot ride on `/api/family`'s token bundle.
   */
  claimRank: async () => {
    if (get().busy) return
    await act(set, get, 'rank.promote', (t) => api.claimRank(t), ['rank', 'family'])
  },

  drawLine: async (line) => {
    if (get().busy) return
    const done = await act(set, get, 'rank.line', (t) => api.drawLine(t, line), ['rank', 'next'])
    if (done) {
      set({
        notice:
          line === 'energy'
            ? `The Shift. Your Energy bar runs to ${done.data.energyMax} now, and it will for the Era.`
            : `The Edge. Your Nerve runs to ${done.data.nerveMax} now, and it will for the Era.`,
      })
    }
  },

  takeHouseVouch: async () => {
    if (get().busy) return
    const done = await act(set, get, 'rank.house', (t) => api.takeHouseVouch(t), ['rank', 'family'])
    /*
     * `content/house.json` `taken`, which the route has always answered with and nothing has ever
     * shown. It is the confirmation for the one moment the floor exists for: "It is done and it is
     * written down. Nobody is going to ask you who spoke for you. They will already know."
     *
     * Set AFTER `act`, because `act` clears the channel on the way in.
     */
    if (done?.data.taken) set({ notice: done.data.taken })
  },

  clearOutcome: () => set({ lastJob: null, lastCrime: null }),

  clearWard: () => set({ lastWard: null }),
  clearRefusal: () => set({ refusal: null }),

  /**
   * Raise the session for fifteen minutes, then replay whatever asked.
   *
   * A wrong password is answered by the server with UNAUTHENTICATED and a line in voice, and it
   * is shown IN the prompt rather than as a refusal behind it, because the player is still
   * standing at the prompt and a message somewhere else is a message they will not see.
   */
  elevate: async (password) => {
    if (get().busy) return
    set({ busy: true, elevationError: null })
    try {
      await api.elevate(password)
    } catch (e) {
      set({ busy: false, elevationError: refusalText(e) })
      return
    }
    const held = parked
    parked = null
    set({ busy: false, needsElevation: false, elevationError: null })
    // `act` looks the token up by intent, so the replay picks up whatever the board re-read while
    // the player was typing their password rather than a nonce the first attempt already burned.
    if (held) await act(set, get, held.intent, held.run, held.refresh)
  },

  cancelElevation: () => {
    parked = null
    set({ needsElevation: false, elevationError: null })
  },
  clearNotice: () => set({ notice: null }),

  /**
   * The Beat, derived locally. The schedule is immutable for the Era and was sent once at boot,
   * so every hour after that costs the client nothing at all.
   */
  beatNow: (now) => {
    const s = get()
    if (!s.boot || s.schedule.length === 0) return null
    // Null outside the Era rather than a throw. This runs on a one second tick, so a crash here
    // would be a crash every second, and a client has nothing useful to do about a closed Era
    // except stop drawing a Beat.
    return beatAtOrNull(now, { startsAt: s.boot.era.startsAt }, s.schedule)
  },
}))

/**
 * Every successful response carries the player slice and, on a read that renders buttons, the
 * screen's token bundle. Applying them HERE rather than at each call site is what stops two
 * screens ever disagreeing about the balance.
 *
 * **A bundle is FILED, not assigned.** There is no slot to overwrite any more, so a board with its
 * own intent set no longer has to hide its token from the store to protect somebody else's. That
 * is what `ownsToken` in `net/api.ts` existed for, and it is deleted: it treated the symptom, cost
 * three module-level variables to work around, and could not have covered Home, where seven boards
 * mint at once and every one of them is somebody's "own".
 */
observe((e) => {
  if (e.player) useSession.setState({ me: e.player })
  if (e.tokens) fileGrant(e.tokens)
})

/**
 * File a grant, superseding anything it overlaps and dropping anything already dead.
 *
 * OVERLAP AND NOT EQUALITY. Two reads of the same board mint two bundles over the same intents and
 * only the newer one is spendable in practice, so keeping both would leave a dead token at the
 * front of the list for every re-read. Disjoint grants are kept side by side, which is the whole
 * point: `/api/money`'s three intents and `/api/jobs`'s two coexist because they never collide.
 */
function fileGrant(g: Grant): void {
  const now = serverNow()
  const intents = new Set(g.intents)
  useSession.setState((s) => ({
    grants: [
      { token: g.token, intents: [...g.intents], expiresAt: g.expiresAt },
      ...s.grants.filter((x) => x.expiresAt > now && !x.intents.some((i) => intents.has(i))),
    ],
  }))
}

/** Forget one grant, by token. Called the instant it is spent, landed or refused. */
function dropGrant(token: string): void {
  useSession.setState((s) => ({ grants: s.grants.filter((g) => g.token !== token) }))
}

/** The live grant that covers this intent, or null. Newest first, so the newest wins. */
function grantFor(intent: ActionIntent): Grant | null {
  const now = serverNow()
  return useSession.getState().grants.find((g) => g.expiresAt > now && g.intents.includes(intent)) ?? null
}

/**
 * WHICH READ MINTS WHICH INTENT, so a button can go and get its own token.
 *
 * Every entry here is the read that a `PlayerDO.issueToken` call sits behind on the server. It
 * exists because a token can be legitimately absent: a token expires after two minutes, a player
 * who leaves a tab open over lunch has none, and a panel that mounts on a screen whose read has
 * not landed yet has none either. The old code answered all three by returning silently from the
 * store action, which on screen is a button that does nothing forever.
 *
 * `market.*` reads whichever aisle is open, because the board is sharded per category and there is
 * no such thing as a market token that is not for an aisle.
 */
const MINTED_BY: Record<ActionIntent, ((s: SessionState) => Promise<void>) | undefined> = {
  'player.district': (s) => s.loadCrimes(),
  'crime.commit': (s) => s.loadCrimes(),
  'job.run': (s) => s.loadJobs(),
  'job.claim': (s) => s.loadJobs(),
  'money.launder': (s) => s.loadMoney(),
  'money.tier': (s) => s.loadMoney(),
  'money.fence': (s) => s.loadMoney(),
  'money.refill': (s) => s.loadMorningLine(),
  'heat.cooloff': (s) => s.loadHeat(),
  'ward.bail': (s) => s.loadWard(),
  'ward.bust': (s) => s.loadWard(),
  'ward.selfbail': (s) => s.loadWard(),
  'ward.seal': (s) => s.loadWard(),
  'market.list': (s) => s.loadMarket(s.market?.category ?? 'all'),
  'market.buy': (s) => s.loadMarket(s.market?.category ?? 'all'),
  'market.cancel': (s) => s.loadMarket(s.market?.category ?? 'all'),
  'market.black': (s) => s.loadMarket(s.market?.category ?? 'all'),
  'gear.equip': (s) => s.loadGear(),
  'gear.repair': (s) => s.loadGear(),
  'gear.crush': (s) => s.loadGear(),
  'business.collect': (s) => s.loadGear(),
  'business.upgrade': (s) => s.loadGear(),
  'territory.collect': (s) => s.loadTerritory(),
  'territory.raid': (s) => s.loadTerritory(),
  'territory.fortify': (s) => s.loadTerritory(),
  'territory.abandon': (s) => s.loadTerritory(),
  'war.declare': (s) => s.loadTerritory(),
  'war.answer': (s) => s.loadTerritory(),
  'war.operation': (s) => s.loadTerritory(),
  'family.books': (s) => s.loadTerritory(),
  'family.apply': (s) => s.loadFamily(),
  'family.decide': (s) => s.loadFamily(),
  'family.role': (s) => s.loadFamily(),
  'family.crew': (s) => s.loadFamily(),
  'family.kick': (s) => s.loadFamily(),
  'family.leave': (s) => s.loadFamily(),
  'family.deposit': (s) => s.loadFamily(),
  'family.withdraw': (s) => s.loadFamily(),
  'family.upgrade': (s) => s.loadFamily(),
  'family.announce': (s) => s.loadFamily(),
  'family.chat': (s) => s.loadFamily(),
  'family.sponsor': (s) => s.loadFamily(),
  'family.create': (s) => s.loadFamily(),
  'family.tithe': (s) => s.loadFamily(),
  'family.grant': (s) => s.loadFamily(),
  'family.gift': (s) => s.loadFamily(),
  'family.vouch': (s) => s.loadFamily(),
  'rank.promote': (s) => s.loadRank(),
  'rank.house': (s) => s.loadRank(),
  'rank.line': (s) => s.loadRank(),
  'pvp.attack': (s) => s.loadPvp(),
  'pvp.sleep': (s) => s.loadPvp(),
  'pvp.ambush': (s) => s.loadPvp(),
  'pvp.vendetta': (s) => s.loadPvp(),
  'pvp.bounty': (s) => s.loadHitlist(),
  'ledger.equip': (s) => s.loadLedger(),
  'onboarding.dismiss': (s) => s.loadNext(),
  /*
   * The rest are intents the CLIENT cannot mint for, and saying so in the table beats leaving a
   * hole in it. Three shapes, all legitimate:
   *
   *   - no route yet: the contract surface, the four unbuilt war verbs, the sit-down, the two
   *     territory verbs (`scout`, `defend`) that no read mints and no route accepts.
   *   - minted by a read this store does not own: `ledger.purchase` comes off `api.purchases()`,
   *     which `Ledger.tsx` calls inline because a checkout must not share a nonce with anything.
   *   - reached only through another verb: `pvp.hospital`.
   *
   * `undefined` here is not a gap. It means "there is no read to send them to", and `act()` says
   * so in voice rather than reloading a board that would not help.
   */
  'contract.accept': undefined,
  'contract.complete': undefined,
  'contract.abandon': undefined,
  'contract.reroll': undefined,
  'territory.scout': undefined,
  'territory.defend': undefined,
  'pvp.sitdown': undefined,
  'pvp.hospital': undefined,
  'war.sabotage': undefined,
  'war.intel': undefined,
  'war.stockpile': undefined,
  'war.strategy': undefined,
  'war.chat': undefined,
  'ledger.claim': undefined,
  'ledger.purchase': undefined,
}

function refusalText(e: unknown): string {
  if (e instanceof ApiError) return e.message
  return 'Something went wrong on our end.'
}



/** The boards `act()` knows how to re-read after a mutation. */
type Screen =
  | 'money'
  | 'heat'
  | 'market'
  | 'gear'
  | 'family'
  | 'rank'
  | 'ledger'
  | 'next'
  | 'crimes'
  | 'jobs'
  | 'ward'
  | 'morning'
  | 'territory'
  | 'pvp'
  | 'hitlist'

/**
 * The action that asked for elevation, held until the password comes back.
 *
 * Module scope rather than store state: it is a function, nothing renders from it, and putting a
 * closure in a zustand slice invites somebody to serialise the store one day and find out.
 */
let parked: {
  intent: ActionIntent
  run: (token: string) => Promise<{ nextToken: string | null }>
  refresh: Screen[]
} | null = null

/**
 * ONE MUTATION, DONE ONCE, and the token is looked up rather than handed in.
 *
 * Five steps now, and the first one is the fix. The caller names the INTENT and `act` finds the
 * grant that covers it; before, every caller reached into the single shared token slot and spent
 * whatever happened to be in it, which on Home was a coin flip between seven boards. See `grants`.
 *
 *   1. find the live grant for this intent, fetching the read that mints it if there is none
 *   2. send, with that grant's token
 *   3. drop the grant, landed or refused, because the server consumed it either way
 *   4. file the `nextToken` under THIS INTENT ONLY, which is all the server minted it for
 *   5. re-read the boards that changed, whether it landed or not
 */
/**
 * BUY, THEN WEAR. Phase 21, DESIGN-DELTAS 1ab.3.
 *
 * A coat bought on the board went into the kit and stayed there until the player found the Kit
 * tab. When the slot it fits is EMPTY there is no decision to make, so the wear is made for them,
 * as a second tokened verb after the buy: the buy is durable before the equip is asked for, and
 * an equip that is refused leaves a bought coat in the kit, which is where it was going anyway.
 * When the slot is worn there IS a decision (the old one may be better, or repaired), so the line
 * says where the choice is rather than making it.
 */
async function wearIfEmpty(
  set: (partial: Partial<SessionState>) => void,
  get: () => SessionState,
  category: string,
  itemId: string,
  how: 'bought' | 'dropped' = 'bought',
): Promise<void> {
  if (category !== 'weapons' && category !== 'armor' && category !== 'vehicles') return
  const rows = get().gear?.items ?? []
  const bought = rows.find((r) => r.itemId === itemId && !r.equipped && r.power > 0)
  if (!bought) return
  const wornInSlot = rows.some((r) => r.category === category && r.equipped)
  if (wornInSlot) {
    set({ notice: `${bought.name} is in your kit. Wear It is on the Kit tab, next to what you have on.` })
    return
  }
  await get().equip(bought.slotId, bought.itemId)
  if (get().refusal === null) {
    set({ notice: how === 'dropped' ? `It fell out of the job, and you are wearing it. ${bought.name} is on.` : `Bought, and wearing it. ${bought.name} is on.` })
  }
}

/**
 * WEAR-FROM-THE-DROP, Phase 25 (DESIGN-DELTAS 1af.1). A piece of gear that fell out of a job or a
 * crime is worn on the spot when its slot is empty, by the same rule a bought one is (1ab.3),
 * because "a Rare fell out and it is sitting in a tab" is the thing Phase 20 fixed for the plate
 * and left for the pockets. The pockets are re-read first: the drop is authoritative on the
 * outcome, but the slot id it landed in is only on the gear read.
 */
async function wearDrops(
  set: (partial: Partial<SessionState>) => void,
  get: () => SessionState,
  drops: { itemId: string; qty: number }[] | null,
): Promise<void> {
  if (!drops || drops.length === 0) return
  const catalog = get().catalog
  const gear = drops.filter((d) => {
    const c = catalog.get(d.itemId)?.category
    return c === 'weapons' || c === 'armor' || c === 'vehicles'
  })
  if (gear.length === 0) return
  await get().loadGear().catch(() => undefined)
  for (const d of gear) await wearIfEmpty(set, get, catalog.get(d.itemId)?.category ?? '', d.itemId, 'dropped')
}

async function act<T extends { nextToken: string | null }>(
  set: (partial: Partial<SessionState>) => void,
  get: () => SessionState,
  intent: ActionIntent,
  run: (token: string) => Promise<T>,
  refresh: Screen[],
): Promise<T | null> {
  set({ busy: true, refusal: null, notice: null })

  /*
   * A MISSING TOKEN IS A STATE A PLAYER CAN REACH BY DOING NOTHING, so it is recovered from rather
   * than returned from. Tokens live two minutes; a tab left open through a phone call has none.
   * Every store action used to answer that with a bare `return`, which is a button that does
   * nothing, forever, with no console line and no message on screen.
   */
  let grant = grantFor(intent)
  if (!grant) {
    const mint = MINTED_BY[intent]
    if (mint) await mint(get()).catch(() => undefined)
    grant = grantFor(intent)
  }
  if (!grant) {
    set({ busy: false, refusal: 'That board has not come back yet. Give it a second and try again.' })
    return null
  }
  const spent = grant.token
  dropGrant(spent)
  /*
   * The OUTCOME is returned, not only the token. It used to be dropped on the floor.
   *
   * `r.data` was bound and never read, so every authored line a mutation answers with died here:
   * `POST /api/rank/house` returns the house's own confirmation and the sponsor who gave it, both
   * typed on the response, and neither reached a screen. An authored string that validates on
   * every build and is thrown away one line after arriving is the same failure as one that is
   * never bundled, and harder to notice.
   */
  let out: T | null = null
  try {
    const r = await run(spent)
    out = r
    /*
     * FILED UNDER THIS INTENT AND NO OTHER. `PlayerDO.envelope` mints `nextToken` with
     * `issueTokenSync([intent])`, one intent, the one just spent, and `ApiOk.nextToken`'s own
     * comment says "bound to the SAME intent as the request that produced it". The old code kept
     * the spent bundle's intent list and swapped the string in, so after one launder the client
     * held a token it believed covered `money.tier` and `money.fence` and the next tap on either
     * was a 409 that looked like a different bug.
     *
     * The expiry is derived rather than sent: `nextToken` is a bare string on the envelope, so
     * `ACTION_TOKEN_TTL_MS` is the only thing that knows when it dies. It is now in `shared`.
     */
    if (r.nextToken) {
      fileGrant({ token: r.nextToken, intents: [intent], expiresAt: serverNow() + ACTION_TOKEN_TTL_MS })
    }
  } catch (e) {
    /*
     * THE LINEUP'S TRIGGER, and it is the refusal itself rather than a poll.
     *
     * `PlayerDO.begin` refuses every tokened verb with `field: 'lineup'` while a challenge is
     * open, and this is the one place every mutation in the game funnels through. So the server
     * says "the desk sergeant wants a word first" and the client goes and gets the interstitial,
     * which means a Lineup issued mid-session reaches a player on their very next action rather
     * than on their next page load. No polling, no socket, no timer.
     */
    if (e instanceof ApiError && e.code === 'CONFLICT' && e.field === 'lineup') {
      const l = await api.lineup().catch(() => null)
      if (l) set({ lineup: l })
    }
    /*
     * NEEDS_ELEVATION is not a refusal, it is a REQUEST, and showing it as a refusal is what made
     * the two guarded verbs look broken. "Enter your password again before you move that kind of
     * money" is a sentence with no control behind it unless something asks for the password. The
     * action is parked here and replayed by `elevate` once the server has raised the session.
     */
    if (e instanceof ApiError && e.code === 'NEEDS_ELEVATION') {
      parked = { intent, run, refresh }
      set({ needsElevation: true, elevationError: null, refusal: null })
    } else {
      set({ refusal: refusalText(e) })
    }
  } finally {
    // The boards are re-read whether it landed or not: a refusal usually means somebody else
    // moved first, and showing the stale board they beat you to is worse than showing nothing.
    for (const screen of refresh) {
      const g = get()
      if (screen === 'money') await g.loadMoney().catch(() => undefined)
      if (screen === 'heat') {
        // The Scanner is one screen with two reads and they are independent: the Heat panel is
        // the player's own state and the band is the city's. Neither should hold the other up.
        await Promise.all([
          g.loadHeat().catch(() => undefined),
          g.loadScanner().catch(() => undefined),
        ])
      }
      if (screen === 'gear') await g.loadGear().catch(() => undefined)
      if (screen === 'family') await g.loadFamily().catch(() => undefined)
      if (screen === 'rank') await g.loadRank().catch(() => undefined)
      if (screen === 'ledger') await g.loadLedger().catch(() => undefined)
      // The panel that answers "what do I do now" is re-read after anything that could have
      // satisfied a beat, which is nearly everything: the predicates are counts over tables the
      // player's own object keeps and every verb in the game writes one of them.
      if (screen === 'next') await g.loadNext().catch(() => undefined)
      // The Ward is re-read whether the verb landed or not: a refusal usually means somebody else
      // got there first, and showing the stale row they beat you to is worse than showing nothing.
      if (screen === 'ward') await g.loadWard().catch(() => undefined)
      if (screen === 'morning') await g.loadMorningLine().catch(() => undefined)
      // Ground changes hands under other people's hands, so the map is re-read after any verb
      // that could have moved it, landed or refused.
      if (screen === 'territory') await g.loadTerritory().catch(() => undefined)
      // The crime board and its odds are both district scoped, so a move has to re-read them.
      if (screen === 'crimes') await g.loadCrimes().catch(() => undefined)
      // Runs, mastery and the gate on the next step all moved, and none of them is in the envelope.
      if (screen === 'jobs') await g.loadJobs().catch(() => undefined)
      /*
       * The Fight List is re-read after every intent, landed or refused, and it is the board most
       * likely to have moved under the player's hand: the cap they just spent, the hospital timer
       * they just started and the shield somebody else's swing just ended are all on it.
       */
      if (screen === 'pvp') await g.loadPvp().catch(() => undefined)
      if (screen === 'hitlist') await g.loadHitlist().catch(() => undefined)
      /*
       * THE AISLE THE PLAYER IS LOOKING AT, which is not necessarily the aisle they just acted in.
       *
       * The category used to be threaded in from the call site, and on the merged `all` board the
       * call site's category is the LISTING's shard: buying a car off All would have re-read the
       * vehicles aisle and swapped the board out from under the player. `market.category` is what
       * the last read was for, which is exactly the board on screen.
       */
      if (screen === 'market') await g.loadMarket(g.market?.category ?? 'all').catch(() => undefined)
    }
    set({ busy: false })
  }
  return out
}

/** The clock every countdown uses. Server time, not the player's watch. */
export { serverNow }
